import { connect, useDispatch, useSelector } from "react-redux";
import Cropper, { ReactCropperElement } from "react-cropper";
import { useLocation, useNavigate, useParams } from "react-router";
import React, {
  createRef,
  Fragment,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import IssuesBoundingBox from "./IssuesBoundingBox";
import ContentLoad from "views/uikit/ContentLoad";
import ImageCarousel from "./ImageCarousel";
import BoundingBox from "./BoundingBox";

import {
  getImageData,
  getImageDataLoader,
  getSelectedIssues,
  getSelectedLabels,
} from "redux/Modals/selectors";
import { State } from "redux/store";
import { getSelectedObjectLabels } from "redux/SingleImage/selectors";
import {
  getImageDisplayOptions,
  getImagePageParamsContext,
  getNavigationCluster,
  getNavigationClusterLoader,
  getNavigationContext,
  isRegionSearchEnabled,
} from "redux/SingleDataset/selectors";
import {
  clearImagePageParamsContext,
  fetchDatasetContextualLabels,
  searchImageFileUploaded,
  setContentLoader,
  setImagePageParamsContext,
  setScrollApplied,
} from "redux/SingleDataset/actions";

import { calculateAspectRatio } from "helpers/utility/utilities";
import {
  setImageDataLoader,
  setSelectedCarouselIndex,
  setSelectedIssues,
  setSelectedLabels,
} from "redux/Modals/actions";
import { IMAGE_DETAILS_PAGE } from "helpers/constants/pages";

import { BiArrowBack } from "react-icons/bi";
import FindSimilarIcon from "assets/icons/FindSimilarIcon";
import {
  OBJECT_ID_QUERY_PARAMETER,
  PAGE_PARAMETER,
  VERTEX_ID,
  VERTEX_SIMILARITY,
  VERTEX_TYPE,
  VIDEO_CLUSTER_ID_PARAMETER,
  VIDEO_ID_PARAMETER,
  VQL_PARAMETER,
} from "helpers/constants/miscellaneous";
import classNames from "classnames";
import { singleImageObjectType } from "types";
import styles from "./style.module.scss";
import "./vl-cropper.css";
import ZoomControl from "./ZoomControl";
import useImageDragAndZoom from "hooks/useImageDragAndZoom";
import useAllQueryParamsFilters from "hooks/useAllQueryParamsFilters";
import {
  ROISquare,
  roiCornerRadius,
} from "../../../../components/ROISquare/ROISquare";
import {
  DefectAreaMask,
  OverlayControls,
  useDefectAreaOverlay,
} from "views/components/DefectAreaOverlay";
import { useGetVideoFramesApiV1ExploreDatasetIdVideoVideoIdFramesGet } from "../../../../../vl-api/vlComponents";
import { FrameData, FramesCarousel } from "./FramesCarousel/FramesCarousel";
import { VideoFramesResponse } from "../../../../../vl-api/vlSchemas";
import { useHotkeys } from "react-hotkeys-hook";
import { VisualSimilarityIcon } from "../../CardsView/DataPageFilters/SearchContainer/Icons";

const MIN_ZOOM_LEVEL = 100;
const ZOOM_MULTIPLIER = 50; //max 50x zoom
const FRAMES_CAROUSEL_HEIGHT = 142;

const extractFrames = (data: VideoFramesResponse) =>
  data.frames
    .map(({ previews }) => previews!)
    .flat()
    .filter((m) => m.type === "IMAGE");

const ImageSection = ({
  imageData,
  navigationCluster,
  children,
  isExpanded,
  setIsExpanded,
  selectedLabels,
  selectedIssues,
  isContentLoading,
  navigationContext,
  regionSearchEnabled,
  imagePageParamsContext,
  navigationClusterLoading,
  videoId,
}: any) => {
  const { searchRegion } = imagePageParamsContext;
  const queryParamsFilters = useAllQueryParamsFilters();

  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const [hasVideoPlayed, setHasVideoPlayed] = useState(false);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const [videoTimestamp, setVideoTimestamp] = useState(0);
  const [videoUri, setVideoUri] = useState("");

  const [imageLoader, setimageLoader] = useState(true);
  const [tooltipDimensions, setTooltipDimensions] = useState({
    height: 0,
    width: 0,
  });
  const [imgDimensions, setImgDimensions] = useState<{
    height: any;
    width: any;
  }>({ height: 0, width: 0 });
  const [imageContainerStyle, setImageContainerStyle] = useState<any>({});
  const [selectionButtonPosition, setSelectionButtonPosition] =
    useState<any>(null);
  const [cropData, setCropData] = useState<any>(null);
  const [file, setFile] = useState<File | null>(null);
  const [hoveredLabelKey, setHoveredLabelKey] = useState("");
  const [maxZoom, setMaxZoom] = useState(500);

  // Displayed hero image size; it is rendered with object-fit: contain
  const [heroImageRect, setHeroImageRect] = useState({
    width: 0,
    height: 0,
    scale: 0,
  });
  const { roiSquareSize: roiSize, centerCropSize } = useSelector(
    getImageDisplayOptions,
  );
  const roiSquareSize = roiSize * heroImageRect.scale;

  const cropperRef = createRef<ReactCropperElement>();
  const heroImageRef = useRef<HTMLImageElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const mainContainerRef = useRef<HTMLDivElement>(null);
  const draggableContainerRef = useRef<HTMLDivElement>(null);
  const isLoading = isContentLoading || imageLoader || navigationCluster;

  const {
    centerImageHandler,
    setZoomTo,
    translatePosition,
    newDimensions,
    isZooming,
    imageZoomInLevel,
    setImageZoomInLevel,
  } = useImageDragAndZoom(
    maxZoom,
    isLoading,
    draggableContainerRef,
    MIN_ZOOM_LEVEL,
    !Boolean(videoId),
  );

  const { datasetId, imageId } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const clusterId = searchParams.get(VIDEO_CLUSTER_ID_PARAMETER) || undefined;

  useEffect(() => {
    setHasVideoPlayed(false);
    setIsVideoPlaying(false);
    setIsFindSimilarButtonSelected(false);
  }, [imageData]);

  useEffect(() => {
    setVideoUri("");
  }, [videoId]);

  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.currentTime = videoTimestamp;
    }
  }, [videoTimestamp, videoId]);

  const { data: videoData } =
    useGetVideoFramesApiV1ExploreDatasetIdVideoVideoIdFramesGet(
      {
        pathParams: { datasetId: String(datasetId), videoId: String(videoId) },
      },
      { enabled: Boolean(videoId), retry: 2 },
    );

  const vqlString = searchParams.get(VQL_PARAMETER);

  const { data: filteredVideoData } =
    useGetVideoFramesApiV1ExploreDatasetIdVideoVideoIdFramesGet(
      {
        pathParams: { datasetId: String(datasetId), videoId: String(videoId) },
        queryParams: { vql: vqlString, cluster_id: clusterId },
      },
      { enabled: Boolean(videoId && vqlString), retry: 2 },
    );

  const frames = useMemo<FrameData[]>(() => {
    if (!videoData) {
      return [];
    }
    return extractFrames(videoData);
  }, [videoData]);

  const framesFromSearch = useMemo<FrameData[]>(() => {
    if (!vqlString || !filteredVideoData) {
      return [];
    }
    return extractFrames(filteredVideoData).sort(
      (a, b) => a.relevance_score! - b.relevance_score!,
    );
  }, [vqlString, filteredVideoData]);

  const [isFindSimilarButtonSelected, setIsFindSimilarButtonSelected] =
    useState(!!searchRegion);

  const timestamp = imageData?.frame_timestamp || 0;

  const handleImageDetailsBackButtonClick = (e: any) => {
    dispatch(setScrollApplied(false));
    dispatch(setSelectedLabels([]));
    dispatch(setSelectedIssues([]));
    setIsExpanded(e, true);
    dispatch(setSelectedCarouselIndex(0));

    const searchParams = new URLSearchParams(location.search);

    if (
      navigationContext.length === 1 &&
      navigationContext.includes(IMAGE_DETAILS_PAGE)
    ) {
      let path = location.pathname.split("/image")[0];
      if (queryParamsFilters.flat_response) {
        path = path.split("/cluster")[0];
      }
      searchParams.delete(VIDEO_ID_PARAMETER);
      searchParams.delete(VIDEO_CLUSTER_ID_PARAMETER);
      searchParams.delete(OBJECT_ID_QUERY_PARAMETER);
      navigate(`${path}?${searchParams.toString()}`);
      dispatch(
        fetchDatasetContextualLabels(datasetId, queryParamsFilters) as any,
      );
    } else navigate(-1);
  };

  const findSimilarButtonClassNames = classNames(styles.findSimilarButton, {
    [styles.blueBackground]: isFindSimilarButtonSelected,
  });

  const resetZoomLevel = () => {
    centerImageHandler();
    setImageZoomInLevel(100);
  };

  const handleVideoPlay = () => {
    if (
      imageZoomInLevel !== 100 ||
      translatePosition.x ||
      translatePosition.y
    ) {
      videoRef.current?.pause();
      return;
    }
    setIsVideoPlaying(true);
    setHasVideoPlayed(true);
    resetZoomLevel();
  };

  const calculateNewImageDimensions = () => {
    if (heroImageRef.current) {
      let zoomValue = imageZoomInLevel / 100;
      let newValues = calculateAspectRatio(heroImageRef.current);
      setTooltipDimensions({
        height: newValues[0] * zoomValue,
        width: newValues[1] * zoomValue,
      });
    }
  };

  const calculateOuterImageContainerHeight = () => {
    const mainContainer = mainContainerRef.current;
    if (!mainContainer) return;

    let carouselHeight = 0;
    if (navigationCluster?.previews?.length > 1) {
      carouselHeight = isExpanded ? 134 : 40;
    }

    const newHeight =
      mainContainer.clientHeight -
      50 - // -50 for image header
      carouselHeight -
      (videoId ? FRAMES_CAROUSEL_HEIGHT : 0);
    if (newHeight > 0) {
      setImageContainerStyle({ height: newHeight });
    }
  };

  const handleImageLoad = () => {
    calculateOuterImageContainerHeight();
    let zoomValue = imageZoomInLevel / MIN_ZOOM_LEVEL;

    const image = heroImageRef.current!;
    const { naturalHeight, naturalWidth, clientHeight, clientWidth } = image;

    setImgDimensions({
      height: naturalHeight * zoomValue,
      width: naturalWidth * zoomValue,
    });
    setimageLoader(false);

    // set max zoom based on aspect ratio and natural height,width of the image
    const zoomWidth = naturalWidth / clientWidth;
    const zoomHeight = naturalHeight / clientHeight;

    const baseZoom = Math.max(zoomWidth, zoomHeight);
    const requiredZoom = Math.floor(baseZoom * ZOOM_MULTIPLIER * 100); // <-- deep zoom
    setMaxZoom(Math.max(requiredZoom, MIN_ZOOM_LEVEL));
    const displayScale = Math.min(
      clientWidth / naturalWidth,
      clientHeight / naturalHeight,
    );
    setHeroImageRect({
      width: naturalWidth * displayScale,
      height: naturalHeight * displayScale,
      scale: displayScale,
    });
  };

  const handleFindSimilarClickOnImagePage = () => {
    if (!isFindSimilarButtonSelected) {
      centerImageHandler();
      setIsFindSimilarButtonSelected(!isFindSimilarButtonSelected);
      dispatch(clearImagePageParamsContext());
    } else {
      handleSelectionSubmit();
    }
  };

  // ==========================================
  // DEPRECATED CROPPER FUNCTIONS (FF OFF)
  // ==========================================

  const handleCropperSubmit = async () => {
    if (typeof cropperRef.current?.cropper !== "undefined" && imageData) {
      // TODO: if video - create frame image file using saveVideoFrameToFile, and then get then coords from cropper
      // and use the file + coords from the new UPLOAD find similar

      const cropper = cropperRef.current.cropper;
      const croppedCanvas = cropper.getCroppedCanvas();
      const { naturalHeight: imageHeight, naturalWidth: imageWidth } =
        cropper.getImageData();

      const createFile = async (imgUrl: any) => {
        const response = await fetch(imgUrl);
        const blob = await response.blob();
        const file = new File([blob], imageData.file_name, {
          type: blob.type,
        });

        return file;
      };

      const imageFile = await createFile(imageData.image_uri);
      if (imageFile) setFile(imageFile);

      croppedCanvas.toBlob((blob: any) => {
        const cropFile = new File([blob], imageData.file_name, {
          type: "image/jpeg",
        });
        // Update state with the cropped file
        const { x, y, width, height } = cropper.getData();
        const absoluteHeightDifference = Math.abs(imageHeight - height);
        const absoluteWidthDifference = Math.abs(imageWidth - width);
        const sameHeight =
          absoluteHeightDifference >= 0 && absoluteHeightDifference <= 1;
        const sameWidth =
          absoluteWidthDifference >= 0 && absoluteWidthDifference <= 1;
        let interacted = false;
        if (!sameHeight || !sameWidth) interacted = true;
        setCropData({
          x: Math.round(x),
          y: Math.round(y),
          x1: Math.round(width) + Math.round(x),
          y1: Math.round(height) + Math.round(y),
          file: cropFile,
          interacted,
        });
      }, "image/jpeg");
    }
  };

  const setupRegionSearchParams = () => {
    searchParams.delete(OBJECT_ID_QUERY_PARAMETER);
    searchParams.delete(VIDEO_ID_PARAMETER);
    searchParams.delete(VIDEO_CLUSTER_ID_PARAMETER);
    dispatch(clearImagePageParamsContext());
  };

  const setupSuccessNavigation = () => {
    dispatch(setContentLoader(false));
    searchParams.set(PAGE_PARAMETER, "1");
    searchParams.delete(VERTEX_SIMILARITY);
  };

  const handleFinalNavigation = (path: string) => {
    navigate(path + `?${searchParams.toString()}`);
    dispatch(
      fetchDatasetContextualLabels(datasetId, queryParamsFilters) as any,
    );
  };

  const handleDeprecatedError = (path: string) => {
    dispatch(setContentLoader(false));
    searchParams.delete(VERTEX_ID);
    searchParams.delete(VERTEX_TYPE);
    searchParams.delete(VERTEX_SIMILARITY);

    handleFinalNavigation(path);
  };

  const handleSelectionSubmit = async (negation?: boolean) => {
    await handleCropperSubmit();
  };

  const renderBoundingBoxes = () => {
    const showBoundingBoxes =
      !isFindSimilarButtonSelected && !isVideoPlaying && !hasVideoPlayed;

    if (!showBoundingBoxes) return null;

    return selectedLabels.map(
      (singleLabel: singleImageObjectType, index: number) => {
        return (
          singleLabel.selected && (
            <BoundingBox
              key={singleLabel.id}
              indexKey={index + ""}
              objectLabel={singleLabel}
              imageDimensions={imgDimensions}
              tooltipDimensions={tooltipDimensions}
              isContentLoading={isContentLoading}
              hoveredLabelKey={hoveredLabelKey}
              setHoveredLabelKey={setHoveredLabelKey}
              handleFindSimilarObjectAsImage={() =>
                handleFindSimilarObjectAsImage(singleLabel)
              }
            />
          )
        );
      },
    );
  };

  const renderIssueBoxes = () => {
    const showIssueBoxes = !isFindSimilarButtonSelected;
    if (showIssueBoxes) {
      return selectedIssues.map((selectedIssue: any, index: number) => (
        <Fragment key={`common-${index}`}>
          <IssuesBoundingBox
            index={index}
            selectedIssue={selectedIssue}
            imageDimensions={imgDimensions}
            tooltipDimensions={tooltipDimensions}
          />
        </Fragment>
      ));
    } else return null;
  };

  const createCroppedFileFromUrl = async (
    url: string,
    boundingBox: number[],
    fileName: string,
  ) => {
    const [x, y, width, height] = boundingBox;

    // 1. Load the image into an HTMLImageElement
    const image = await new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.crossOrigin = "anonymous";
      img.onload = () => resolve(img);
      img.onerror = (err) => reject(err);
      img.src = url;
    });

    // 2. Create a canvas with the dimensions of the crop
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");

    if (!ctx) throw new Error("Could not get canvas context");

    // 3. Draw the specific region of the image onto the canvas
    // drawImage(image, sourceX, sourceY, sourceWidth, sourceHeight, destX, destY, destWidth, destHeight)
    ctx.drawImage(image, x, y, width, height, 0, 0, width, height);

    // 4. Convert canvas to Blob
    const blob = await new Promise<Blob | null>((resolve) => {
      canvas.toBlob((b) => resolve(b), "image/jpeg", 1); // 1 = 100% quality
    });

    if (!blob) throw new Error("Canvas is empty");

    // 5. Convert Blob to File
    return new File([blob], fileName, { type: blob.type });
  };

  useHotkeys("Escape", () => setIsFindSimilarButtonSelected(false));

  // eslint-disable-next-line
  const saveVideoFrameToFile = async (cropCoordinates?: {
    x: number;
    y: number;
    width: number;
    height: number;
  }): Promise<{
    file: File;
    scaledCoordinates?: { x: number; y: number; width: number; height: number };
  }> => {
    return new Promise((resolve, reject) => {
      const videoElement = videoRef.current;

      if (!videoElement) {
        console.error("Video element not found");
        reject(new Error("Video element not available"));
        return;
      }

      // Check if video is loaded and has dimensions
      if (!videoElement.videoWidth || !videoElement.videoHeight) {
        console.error("Video not loaded or has no dimensions", {
          readyState: videoElement.readyState,
          videoWidth: videoElement.videoWidth,
          videoHeight: videoElement.videoHeight,
        });
        reject(new Error("Video not loaded properly"));
        return;
      }

      // Check video ready state
      if (videoElement.readyState < 2) {
        // HAVE_CURRENT_DATA
        console.error("Video not ready for capture", {
          readyState: videoElement.readyState,
        });
        reject(new Error("Video not ready for capture"));
        return;
      }

      const canvas = document.createElement("canvas");
      canvas.width = videoElement.videoWidth;
      canvas.height = videoElement.videoHeight;
      const ctx = canvas.getContext("2d");

      if (!ctx) {
        console.error("Failed to get canvas 2D context");
        reject(new Error("Canvas context not available"));
        return;
      }
      ctx.drawImage(videoElement, 0, 0, canvas.width, canvas.height);

      // Calculate scaling if crop coordinates provided
      let scaledCoordinates:
        | { x: number; y: number; width: number; height: number }
        | undefined;
      if (cropCoordinates) {
        const actualVideoWidth = videoElement.videoWidth;
        const actualVideoHeight = videoElement.videoHeight;
        const videoRect = videoElement.getBoundingClientRect();
        const containerWidth = videoRect.width;
        const containerHeight = videoRect.height;

        const videoAspectRatio = actualVideoWidth / actualVideoHeight;
        const containerAspectRatio = containerWidth / containerHeight;

        let displayedVideoWidth, displayedVideoHeight;
        if (containerAspectRatio > videoAspectRatio) {
          displayedVideoHeight = containerHeight;
          displayedVideoWidth = containerHeight * videoAspectRatio;
        } else {
          displayedVideoWidth = containerWidth;
          displayedVideoHeight = containerWidth / videoAspectRatio;
        }

        const offsetX = (containerWidth - displayedVideoWidth) / 2;
        const offsetY = (containerHeight - displayedVideoHeight) / 2;

        const adjustedX = cropCoordinates.x - offsetX;
        const adjustedY = cropCoordinates.y - offsetY;

        const scaleX = actualVideoWidth / displayedVideoWidth;
        const scaleY = actualVideoHeight / displayedVideoHeight;

        scaledCoordinates = {
          x: adjustedX * scaleX,
          y: adjustedY * scaleY,
          width: cropCoordinates.width * scaleX,
          height: cropCoordinates.height * scaleY,
        };
      }

      canvas.toBlob(
        (blob: any) => {
          const file = new File([blob], "video_frame", {
            type: "image/jpeg",
            lastModified: Date.now(),
          });
          resolve({ file, scaledCoordinates });
        },
        "image/jpeg",
        1,
      );
    });
  };

  const handleFindSimilarObjectAsImage = async (
    objectLabel: singleImageObjectType,
  ) => {
    const [x, y, x1, y1] = objectLabel.boundingBox;
    const imageFile = await createCroppedFileFromUrl(
      imageData.image_uri,
      objectLabel.boundingBox,
      imageData.file_name,
    );
    setFile(imageFile);
    setCropData({
      x,
      y,
      x1,
      y1,
      file: imageFile,
    });
  };

  const renderRegionOfInterest = () => {
    if (isFindSimilarButtonSelected && imageData) {
      const handleButtonStyle = () => {
        if (selectionButtonPosition) {
          return selectionButtonPosition;
        } else return { display: "none" };
      };

      const handleCropEvent = () => {
        if (typeof cropperRef.current?.cropper !== "undefined") {
          const data = cropperRef.current?.cropper.getCropBoxData();
          setSelectionButtonPosition({
            top: data.top + data.height / 2 - 15,
            left: data.left + data.width / 2 - 15,
          });
        }
      };
      const handleCropperReady = () => {
        const ref = cropperRef.current;
        if (searchRegion && isFindSimilarButtonSelected && ref) {
          const boundingBoxValues = searchRegion.split(",");
          if (typeof ref.cropper !== "undefined") {
            const cropper = ref.cropper;

            cropper.setCropBoxData({
              left: parseInt(boundingBoxValues[0]),
              top: parseInt(boundingBoxValues[1]),
              width: parseInt(boundingBoxValues[2]),
              height: parseInt(boundingBoxValues[3]),
            });
          }
        }
      };

      if (imageZoomInLevel > MIN_ZOOM_LEVEL) {
        resetZoomLevel();
      }

      return (
        <div className={styles.cropperContainer}>
          <div
            className={styles.submitSelectionButton}
            onClick={() => handleSelectionSubmit()}
            style={handleButtonStyle()}
          >
            <div className={styles.submitSelectionButtonIcon}>
              <FindSimilarIcon />
            </div>
          </div>
          <Cropper
            className={styles.cropper}
            ref={cropperRef}
            preview=".img-preview"
            src={
              videoId
                ? `data:image/svg+xml;utf8,<svg xmlns="http://www.w3.org/2000/svg" width="${draggableContainerRef.current?.clientWidth}" height="${draggableContainerRef.current?.clientHeight}"></svg>`
                : imageData.image_uri
            }
            viewMode={1}
            minCropBoxHeight={50}
            minCropBoxWidth={50}
            background={false}
            responsive={true}
            autoCropArea={searchRegion ? 0 : 100}
            checkOrientation={true}
            zoomable={false}
            guides={false}
            ready={handleCropperReady}
            crop={handleCropEvent}
          />
        </div>
      );
    } else return null;
  };

  const handleRegionSearch = async () => {
    if (cropData && file) {
      const boundingBoxValues = `${cropData.x},${cropData.y},${cropData.x1},${cropData.y1}`;
      const path = `/dataset/${datasetId}/data`;
      setupRegionSearchParams();

      const cropper = cropperRef.current?.cropper;
      if (cropper !== undefined) {
        const { left, top, width, height } = cropper.getCropBoxData();
        const searchRegionValues = `${Math.round(left)},${Math.round(
          top,
        )},${Math.round(width)},${Math.round(height)}`;

        dispatch(
          setImagePageParamsContext({
            imageId: imageId!,
            searchRegion: searchRegionValues,
          }),
        );
      }
      dispatch(
        searchImageFileUploaded(
          datasetId,
          file,
          queryParamsFilters,
          cropData.file,
          cropData.interacted ? boundingBoxValues : undefined,
        ) as any,
      )
        .then((anchorMediaId: string) => {
          setupSuccessNavigation();
          searchParams.set(VERTEX_ID, anchorMediaId);
          searchParams.set(VERTEX_TYPE, "UPLOAD");
          handleFinalNavigation(path);
        })
        .catch(() => {
          handleDeprecatedError(path);
        });
      dispatch(setImageDataLoader(true));
    }
  };

  useEffect(() => {
    const handleResize = () => {
      requestAnimationFrame(calculateOuterImageContainerHeight);
    };

    window.addEventListener("resize", handleResize);
    // Initial calculation after a small delay to ensure DOM is ready
    const timeoutId = setTimeout(calculateOuterImageContainerHeight, 0);

    return () => {
      window.removeEventListener("resize", handleResize);
      clearTimeout(timeoutId);
    };
    //eslint-disable-next-line
  }, [
    isExpanded,
    isFindSimilarButtonSelected,
    navigationClusterLoading,
    navigationCluster,
  ]);

  // Add a separate effect to handle navigation cluster changes
  useEffect(() => {
    requestAnimationFrame(calculateOuterImageContainerHeight);
    //eslint-disable-next-line
  }, [navigationCluster, newDimensions]);

  useEffect(() => {
    if (imageContainerStyle.height) {
      calculateNewImageDimensions();
    }
    //eslint-disable-next-line
  }, [imageContainerStyle]);

  useEffect(() => {
    handleRegionSearch();
    //eslint-disable-next-line
  }, [cropData]);

  const handleImageChange = (newImg: any) => {
    resetZoomLevel();
    dispatch(setSelectedLabels([]));
    dispatch(setSelectedIssues([]));

    const imageID = newImg.type === "IMAGE" ? newImg.media_id : newImg.image_id;

    if (newImg.video_id) {
      searchParams.set(VIDEO_ID_PARAMETER, newImg.video_id);
      setVideoTimestamp(newImg.frame_timestamp);
    } else {
      searchParams.delete(VIDEO_ID_PARAMETER);
    }
    if (videoRef.current) {
      videoRef.current.pause();
    }

    const path = `${location.pathname.split("image/")[0]}image/${imageID}`;
    const url = `${path}?${searchParams.toString()}`;
    navigate(url, { replace: true });
  };

  const handleSpaceClick = useCallback(() => {
    if (videoRef.current) {
      if (isVideoPlaying) {
        videoRef.current.pause();
      } else {
        videoRef.current.play();
      }
    }
  }, [isVideoPlaying]);

  useHotkeys("Space", handleSpaceClick);

  const showLoader = useMemo(() => {
    if (videoId) {
      return !videoUri;
    }
    return !imageData && (imageLoader || isContentLoading);
  }, [imageData, imageLoader, isContentLoading, videoId, videoUri]);

  // Defect area overlays apply to still images only
  const showDefectOverlays = !videoUri && !isFindSimilarButtonSelected;
  const defectOverlay = useDefectAreaOverlay(showDefectOverlays);

  return (
    <div className={styles.mainWrapper}>
      <div className={styles.mainContainer} ref={mainContainerRef}>
        <div className={styles.imageHeader}>
          <div
            className={styles.backButton}
            onClick={handleImageDetailsBackButtonClick}
          >
            <div className={styles.backButtonIcon}>
              <BiArrowBack size="1.25em" />
            </div>
            <span className={styles.backButtonText}>GO BACK</span>
          </div>
          <div className={styles.imageTitle} title={imageData?.file_name}>
            {imageData?.file_name}
          </div>
          <div className={styles.headerActions}>
            {showDefectOverlays && (
              <OverlayControls
                roiSize={roiSize}
                marginSize={centerCropSize}
                {...defectOverlay.controls}
              />
            )}
            {regionSearchEnabled ? (
              <div
                className={findSimilarButtonClassNames}
                onClick={handleFindSimilarClickOnImagePage}
              >
                <VisualSimilarityIcon />
                <div className={styles.findSimilarButtonText}>Find Similar</div>
              </div>
            ) : (
              <div className={styles.showNoRegionSearch}></div>
            )}
          </div>
        </div>
        <div className={styles.imageContainer} style={imageContainerStyle}>
          <div
            className={styles.draggableContainer}
            ref={isVideoPlaying ? undefined : draggableContainerRef}
          >
            {showLoader ? (
              <div className={styles.imageLoader}>
                <ContentLoad />
              </div>
            ) : (
              <>
                {showDefectOverlays && defectOverlay.maskVisible && (
                  <DefectAreaMask
                    marginSize={centerCropSize * heroImageRect.scale}
                    marginRadius={roiCornerRadius(roiSquareSize)}
                    imageWidth={heroImageRect.width}
                    imageHeight={heroImageRect.height}
                    imageRadius={10}
                  />
                )}
                {
                  <ROISquare
                    size={
                      showDefectOverlays && !defectOverlay.roiVisible
                        ? 0
                        : roiSquareSize
                    }
                  />
                }
                {videoUri && (
                  <video
                    crossOrigin="anonymous"
                    ref={videoRef}
                    src={videoUri}
                    controls={!isFindSimilarButtonSelected}
                    className={classNames(styles.videoPlayer, {
                      [styles.hidden]: imageZoomInLevel !== 100,
                    })}
                    onPlay={handleVideoPlay}
                    onPause={() => setIsVideoPlaying(false)}
                    onLoadedData={() => {
                      videoRef.current!.currentTime = videoTimestamp;
                      setTimeout(() => {
                        setVideoLoaded(true);
                      }, 10);
                    }}
                    onSeeked={() => {
                      if (videoLoaded) {
                        setHasVideoPlayed(true);
                      }
                    }}
                  >
                    error
                  </video>
                )}
                <img
                  ref={heroImageRef}
                  className={classNames(styles.heroImage, {
                    [styles.hidden]: videoId && imageZoomInLevel === 100,
                  })}
                  src={imageData?.image_uri}
                  alt="hero_img"
                  loading="lazy"
                  onLoad={handleImageLoad}
                  onError={(e) => {
                    console.error(
                      `Error loading hero image: ${imageData?.image_uri}`,
                      e,
                    );
                    setimageLoader(false);
                  }}
                  draggable={false}
                />
              </>
            )}
            {renderRegionOfInterest()}
          </div>
          {/* Overlay for bounding boxes and issues, not affected by scale */}
          {!isZooming && (
            <div
              className={styles.boundingBoxOverlay}
              style={{
                width: newDimensions.width || undefined,
                height: newDimensions.height || undefined,
                transform: `translate(${translatePosition.x}px, ${translatePosition.y}px)`,
              }}
            >
              {renderBoundingBoxes()}
              {renderIssueBoxes()}
            </div>
          )}
          {!isFindSimilarButtonSelected && !isVideoPlaying && (
            <ZoomControl
              maxZoom={maxZoom}
              zoomLevel={imageZoomInLevel}
              onZoomLevelChange={setZoomTo}
              onCenterImageClick={centerImageHandler}
              className={styles.zoomController}
            />
          )}
        </div>
        {videoId && (
          <FramesCarousel
            isContentLoading={isContentLoading}
            currentImageId={imageId!}
            frames={frames}
            framesFromSearch={framesFromSearch}
            height={FRAMES_CAROUSEL_HEIGHT}
            onImageChange={handleImageChange}
            initialMode={vqlString ? "results" : "timeline"}
          />
        )}
        {navigationCluster && navigationCluster.previews.length > 1 && (
          <ImageCarousel
            isExpanded={isExpanded}
            setIsExpanded={setIsExpanded}
            navigationCluster={navigationCluster}
            isContentLoading={isContentLoading}
            timestamp={timestamp}
            onImageChange={handleImageChange}
          />
        )}
      </div>

      {children}
    </div>
  );
};

const mapStateToProps = (state: State) => {
  return {
    imageData: getImageData(state),
    selectedLabels: getSelectedLabels(state),
    selectedIssues: getSelectedIssues(state),
    isContentLoading: getImageDataLoader(state),
    navigationContext: getNavigationContext(state),
    navigationCluster: getNavigationCluster(state),
    selectedObjectLabel: getSelectedObjectLabels(state),
    regionSearchEnabled: isRegionSearchEnabled(state),
    imagePageParamsContext: getImagePageParamsContext(state),
    navigationClusterLoading: getNavigationClusterLoader(state),
  };
};

export default connect(mapStateToProps)(ImageSection);
